import {
  AlertTriangle,
  Bot,
  Building2,
  CheckCircle2,
  CreditCard,
  FileText,
  Globe2,
  Layers3,
  Link2,
  Mail,
  Clock3,
  Play,
  Plus,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Workflow,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { formatDate } from "../../lib/format";
import type { BusinessCenterAutomationWorkflowRecord, BusinessCenterCompanyRecord } from "../../lib/types";
import { loadBusinessCenterCompanies, refreshBusinessCenterCompanies } from "./businessCenterCompanyUsersData";
import {
  automationActionOptions,
  automationCategoryOptions,
  automationConditionFieldOptions,
  automationConditionOperatorOptions,
  automationStatusOptions,
  automationTriggerOptions,
  automationWorkspaceOptions,
  buildAutomationWorkflowSummary,
  buildBusinessCenterAutomationDashboard,
  createBusinessCenterAutomationWorkflow,
  loadBusinessCenterAutomationWorkflows,
  refreshBusinessCenterAutomationWorkflows,
  upsertBusinessCenterAutomationWorkflow,
  type AutomationFormInput,
} from "./businessCenterAutomationData";

type AutomationFormState = AutomationFormInput;

const blankForm: AutomationFormState = {
  companyId: "",
  workspaceKey: "all",
  category: "email",
  name: "",
  description: "",
  triggerType: "Record Created",
  triggerEntityType: "client",
  triggerEntityLabel: "Client Record",
  triggerFieldName: "",
  triggerSchedule: "",
  conditionField: "status",
  conditionOperator: "is",
  conditionValue: "active",
  actionType: "Send Email",
  actionLabel: "",
  actionTarget: "",
};

export function AutomationEnginePage() {
  const [companies, setCompanies] = useState<BusinessCenterCompanyRecord[]>([]);
  const [workflows, setWorkflows] = useState<BusinessCenterAutomationWorkflowRecord[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedWorkspace, setSelectedWorkspace] = useState<AutomationFormState["workspaceKey"]>("all");
  const [selectedCategory, setSelectedCategory] = useState<AutomationFormState["category"] | "all">("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [editingWorkflowId, setEditingWorkflowId] = useState<string>("");
  const [form, setForm] = useState<AutomationFormState>(blankForm);

  useAutomationBranding(
    "Automation Engine",
    "Company-scoped automation workflows, triggers, conditions, actions, and run history for Business Center OS.",
  );

  useEffect(() => {
    return loadWorkspace();
  }, []);

  const visibleCompanies = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return companies.filter((company) => {
      if (!normalized) return company.companyType !== "parent";
      return (
        company.companyType !== "parent" &&
        [company.companyName, company.companySlug, company.brand, company.status].join(" ").toLowerCase().includes(normalized)
      );
    });
  }, [companies, search]);

  const selectedCompany = useMemo(() => companies.find((company) => company.id === selectedCompanyId) || null, [companies, selectedCompanyId]);

  const filteredWorkflows = useMemo(() => {
    return workflows.filter((workflow) => {
      const byCompany = !selectedCompanyId || workflow.companyId === selectedCompanyId;
      const byWorkspace = selectedWorkspace === "all" || workflow.workspaceKey === selectedWorkspace || workflow.workspaceKey === "all";
      const byCategory = selectedCategory === "all" || workflow.category === selectedCategory;
      const normalized = search.trim().toLowerCase();
      const bySearch =
        !normalized ||
        [workflow.name, workflow.description, workflow.companyName, workflow.sourceModule, workflow.workspaceKey, workflow.status]
          .join(" ")
          .toLowerCase()
          .includes(normalized);
      return byCompany && byWorkspace && byCategory && bySearch;
    });
  }, [search, selectedCategory, selectedCompanyId, selectedWorkspace, workflows]);

  const globalDashboard = useMemo(() => buildBusinessCenterAutomationDashboard(workflows), [workflows]);
  const scopedDashboard = useMemo(() => buildBusinessCenterAutomationDashboard(filteredWorkflows), [filteredWorkflows]);
  const selectedWorkflow = useMemo(
    () => workflows.find((workflow) => workflow.id === editingWorkflowId) || filteredWorkflows[0] || null,
    [editingWorkflowId, filteredWorkflows, workflows],
  );

  useEffect(() => {
    if (selectedCompanyId && selectedCompany && form.companyId !== selectedCompanyId && !editingWorkflowId) {
      setForm((current) => ({ ...current, companyId: selectedCompanyId, workspaceKey: workspaceForCompany(selectedCompany) }));
    }
  }, [editingWorkflowId, form.companyId, selectedCompany, selectedCompanyId]);

  useEffect(() => {
    if (!selectedWorkflow) return;
    setEditingWorkflowId(selectedWorkflow.id);
    setForm({
      companyId: selectedWorkflow.companyId,
      workspaceKey: selectedWorkflow.workspaceKey,
      category: selectedWorkflow.category,
      name: selectedWorkflow.name,
      description: selectedWorkflow.description,
      triggerType: selectedWorkflow.trigger.type,
      triggerEntityType: selectedWorkflow.trigger.entityType,
      triggerEntityLabel: selectedWorkflow.trigger.entityLabel,
      triggerFieldName: selectedWorkflow.trigger.fieldName || "",
      triggerSchedule: selectedWorkflow.trigger.schedule || "",
      conditionField: selectedWorkflow.conditions[0]?.field || "status",
      conditionOperator: selectedWorkflow.conditions[0]?.operator || "is",
      conditionValue: selectedWorkflow.conditions[0]?.value || "active",
      actionType: selectedWorkflow.actions[0]?.type || "Send Email",
      actionLabel: selectedWorkflow.actions[0]?.label || "",
      actionTarget: selectedWorkflow.actions[0]?.target || "",
    });
  }, [selectedWorkflow?.id]);

  function loadWorkspace() {
    setLoading(true);
    setError("");
    let cancelled = false;
    Promise.all([
      refreshBusinessCenterCompanies().catch(() => loadBusinessCenterCompanies()),
      refreshBusinessCenterAutomationWorkflows().catch(() => loadBusinessCenterAutomationWorkflows()),
    ])
      .then(([nextCompanies, nextWorkflows]) => {
        if (cancelled) return;
        setCompanies(nextCompanies);
        setWorkflows(nextWorkflows);
        const firstCompany = nextCompanies.find((company) => company.companyType !== "parent") || null;
        const currentCompany = nextCompanies.find((company) => company.id === selectedCompanyId) || firstCompany;
        if (currentCompany) {
          setSelectedCompanyId(currentCompany.id);
          setForm((current) => ({ ...current, companyId: currentCompany.id, workspaceKey: workspaceForCompany(currentCompany) }));
        }
        setEditingWorkflowId("");
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load automation workspace.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }

  function updateForm<K extends keyof AutomationFormState>(key: K, value: AutomationFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function selectCompany(companyId: string) {
    const company = companies.find((item) => item.id === companyId);
    if (!company) return;
    setSelectedCompanyId(companyId);
    setEditingWorkflowId("");
    setForm((current) => ({ ...current, companyId: companyId, workspaceKey: workspaceForCompany(company), name: current.name || "" }));
  }

  function resetForm() {
    const currentCompany = companies.find((company) => company.id === selectedCompanyId) || visibleCompanies[0] || null;
    setEditingWorkflowId("");
    setForm({
      ...blankForm,
      companyId: currentCompany?.id || "",
      workspaceKey: currentCompany ? workspaceForCompany(currentCompany) : "all",
      category: currentCompany?.brand === "kreatethedate" ? "kreatethedate" : currentCompany?.brand === "ar_recovery" ? "ar_recovery" : "email",
      name: "",
      description: "",
      triggerType: "Record Created",
      triggerEntityType: currentCompany?.brand === "ar_recovery" ? "billing_record" : "client",
      triggerEntityLabel: currentCompany?.brand === "kreatethedate" ? "Portal Record" : "Client Record",
      conditionField: "status",
      conditionOperator: "is",
      conditionValue: "active",
      actionType: "Send Email",
      actionLabel: "",
      actionTarget: "",
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const nextWorkflow = formToWorkflow(form, workflows, editingWorkflowId);
      upsertBusinessCenterAutomationWorkflow(nextWorkflow);
      setMessage(`${nextWorkflow.name} saved for ${nextWorkflow.companyName}.`);
      loadWorkspace();
      setEditingWorkflowId(nextWorkflow.id);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save automation workflow.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <BusinessCenterOSAdminShell
      title="Automation Engine"
      description="Manage company-scoped automation workflows, triggers, conditions, actions, run history, and approval-ready operating rules across Business Center OS."
      statusItems={[
        { label: "Workflows", value: globalDashboard.totalWorkflows },
        { label: "Active", value: globalDashboard.activeWorkflows },
        { label: "Failed", value: globalDashboard.failedWorkflows },
        { label: "Success rate", value: `${globalDashboard.successRate}%` },
      ]}
    >
      <div className="grid gap-8 text-[#F7FAFF]">
        <section className="grid gap-6 xl:grid-cols-[.88fr_1.12fr]">
          <aside className="grid gap-6">
            <Panel title="Tenant scope" eyebrow="Company-scoped automation" icon={Building2}>
              <div className="grid gap-3">
                <label className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.18em] text-[#93C5FD]">Search companies</span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="focus-ring rounded-2xl border border-[rgba(96,165,250,.18)] bg-[#08111A] px-4 py-3 text-sm text-[#F8FAFC]"
                    placeholder="Kreatethedate, Truevance, AR Recovery..."
                  />
                </label>
                <div className="grid gap-2">
                  {visibleCompanies.map((company) => {
                    const count = globalDashboard.companyCounts[company.companyName] || 0;
                    const active = selectedCompanyId === company.id;
                    return (
                      <button
                        key={company.id}
                        type="button"
                        onClick={() => selectCompany(company.id)}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          active
                            ? "border-cyan-400/60 bg-cyan-400/10"
                            : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-black text-white">{company.companyName}</p>
                          <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#B8D6FF]">
                            {count} workflows
                          </span>
                        </div>
                        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#8CA5C1]">
                          {company.brand} • {company.status}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </Panel>

            <Panel title="Automation categories" eyebrow="Workflow families" icon={Workflow}>
              <div className="grid gap-2">
                <FilterPill label="All categories" active={selectedCategory === "all"} onClick={() => setSelectedCategory("all")} />
                {automationCategoryOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSelectedCategory(option.value)}
                    className={`rounded-2xl border px-4 py-3 text-left transition ${
                      selectedCategory === option.value
                        ? "border-cyan-400/60 bg-cyan-400/10"
                        : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                    }`}
                  >
                    <p className="text-sm font-black text-white">{option.label}</p>
                    <p className="mt-1 text-xs font-semibold leading-6 text-[#A4BDD8]">{option.description}</p>
                  </button>
                ))}
              </div>
            </Panel>

            <Panel title="Recent runs" eyebrow="Operational feed" icon={CheckCircle2}>
              <div className="grid gap-3">
                {globalDashboard.recentRuns.map((run) => (
                  <div key={run.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-black text-white">{run.summary}</p>
                      <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${run.status === "Succeeded" ? "border-emerald-400/40 text-emerald-300" : run.status === "Failed" ? "border-rose-400/40 text-rose-300" : "border-cyan-400/40 text-cyan-300"}`}>
                        {run.status}
                      </span>
                    </div>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#9AB2CB]">
                      {run.triggeredBy} • {formatDate(run.startedAt)}
                    </p>
                  </div>
                ))}
              </div>
            </Panel>
          </aside>

          <div className="grid gap-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <MetricCard label="Scope workflows" value={scopedDashboard.totalWorkflows} note="Company and workspace filtered." />
              <MetricCard label="Scope active" value={scopedDashboard.activeWorkflows} note="Currently enabled automation." />
              <MetricCard label="Scope success" value={`${scopedDashboard.successRate}%`} note="Based on seeded run history." />
              <MetricCard label="Scope upcoming" value={scopedDashboard.upcomingScheduledActions.length} note="Next scheduled runs." />
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.08fr_.92fr]">
              <Panel title="Workflow builder" eyebrow="Triggers, conditions, actions" icon={Bot}>
                <form className="grid gap-4" onSubmit={handleSubmit}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Company">
                      <select value={form.companyId} onChange={(event) => selectCompany(event.target.value)} className="field-input">
                        <option value="">Select a company</option>
                        {visibleCompanies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.companyName}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Workspace">
                      <select value={form.workspaceKey} onChange={(event) => updateForm("workspaceKey", event.target.value as AutomationFormState["workspaceKey"])} className="field-input">
                        {automationWorkspaceOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Category">
                      <select value={form.category} onChange={(event) => updateForm("category", event.target.value as AutomationFormState["category"])} className="field-input">
                        {automationCategoryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Name">
                      <input value={form.name} onChange={(event) => updateForm("name", event.target.value)} className="field-input" placeholder="Proposal Sent Follow-Up" />
                    </Field>
                  </div>

                  <Field label="Description">
                    <textarea value={form.description} onChange={(event) => updateForm("description", event.target.value)} className="field-input min-h-24" placeholder="Describe when this automation runs and why it matters." />
                  </Field>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Trigger">
                      <select value={form.triggerType} onChange={(event) => updateForm("triggerType", event.target.value as AutomationFormState["triggerType"])} className="field-input">
                        {automationTriggerOptions.map((trigger) => (
                          <option key={trigger} value={trigger}>
                            {trigger}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Trigger entity">
                      <input value={form.triggerEntityLabel} onChange={(event) => updateForm("triggerEntityLabel", event.target.value)} className="field-input" placeholder="Lead Record, Project, Account..." />
                    </Field>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Trigger field">
                      <input value={form.triggerFieldName} onChange={(event) => updateForm("triggerFieldName", event.target.value)} className="field-input" placeholder="status, dueDate, amount..." />
                    </Field>
                    <Field label="Condition">
                      <select value={form.conditionField} onChange={(event) => updateForm("conditionField", event.target.value)} className="field-input">
                        {automationConditionFieldOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Operator">
                      <select value={form.conditionOperator} onChange={(event) => updateForm("conditionOperator", event.target.value as AutomationFormState["conditionOperator"])} className="field-input">
                        {automationConditionOperatorOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Condition value">
                      <input value={form.conditionValue} onChange={(event) => updateForm("conditionValue", event.target.value)} className="field-input" placeholder="active, paid, sent..." />
                    </Field>
                    <Field label="Schedule hint">
                      <input value={form.triggerSchedule} onChange={(event) => updateForm("triggerSchedule", event.target.value)} className="field-input" placeholder="Optional cadence or due date hint" />
                    </Field>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Action">
                      <select value={form.actionType} onChange={(event) => updateForm("actionType", event.target.value as AutomationFormState["actionType"])} className="field-input">
                        {automationActionOptions.map((action) => (
                          <option key={action.value} value={action.value}>
                            {action.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Action label">
                      <input value={form.actionLabel} onChange={(event) => updateForm("actionLabel", event.target.value)} className="field-input" placeholder="Send welcome email" />
                    </Field>
                    <Field label="Action target">
                      <input value={form.actionTarget} onChange={(event) => updateForm("actionTarget", event.target.value)} className="field-input" placeholder="Email template, queue, or endpoint" />
                    </Field>
                  </div>

                  {message ? <StatusBanner tone="success">{message}</StatusBanner> : null}
                  {error ? <StatusBanner tone="error">{error}</StatusBanner> : null}

                  <div className="flex flex-wrap gap-3">
                    <button type="submit" disabled={saving} className="business-center-os-button focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold">
                      <Plus className="size-4" aria-hidden="true" />
                      {saving ? "Saving..." : editingWorkflowId ? "Update workflow" : "Create workflow"}
                    </button>
                    <button type="button" onClick={resetForm} className="business-center-os-button business-center-os-button--secondary focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-extrabold">
                      <Play className="size-4" aria-hidden="true" />
                      New draft
                    </button>
                  </div>
                </form>
              </Panel>

              <div className="grid gap-6">
                <Panel title="Selected workflow" eyebrow="Workflow detail" icon={Sparkles}>
                  {selectedWorkflow ? (
                    <div className="grid gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-2xl font-black text-white">{selectedWorkflow.name}</p>
                          <BadgeBadge label={selectedWorkflow.status} />
                        </div>
                        <p className="mt-2 text-sm leading-7 text-[#C7D6EB]">{selectedWorkflow.description}</p>
                      </div>
                      <div className="grid gap-2 rounded-3xl border border-white/10 bg-white/5 p-4">
                        <DetailRow label="Company" value={selectedWorkflow.companyName} />
                        <DetailRow label="Workspace" value={selectedWorkflow.workspaceKey} />
                        <DetailRow label="Trigger" value={buildAutomationWorkflowSummary(selectedWorkflow).triggerLabel} />
                        <DetailRow label="Condition" value={buildAutomationWorkflowSummary(selectedWorkflow).conditionSummary} />
                        <DetailRow label="Action" value={buildAutomationWorkflowSummary(selectedWorkflow).actionSummary} />
                        <DetailRow label="Next run" value={buildAutomationWorkflowSummary(selectedWorkflow).scheduleLabel} />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-semibold leading-7 text-[#AFC1D9]">Select a workflow to inspect triggers, conditions, and actions.</p>
                  )}
                </Panel>

                <Panel title="Future-proof integrations" eyebrow="Platform adapters" icon={Globe2}>
                  <div className="grid grid-cols-2 gap-3">
                    {["Brevo", "Stripe", "Calendly", "Google Workspace", "Twilio", "QuickBooks", "Cloudflare", "Memory Media Vault"].map((integration) => (
                      <div key={integration} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-white">
                        {integration}
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            </div>

            <Panel title="Seeded workflows" eyebrow="Reusable platform capability" icon={Workflow}>
              <div className="grid gap-3">
                {filteredWorkflows.map((workflow) => {
                  const summary = buildAutomationWorkflowSummary(workflow);
                  const active = selectedWorkflow?.id === workflow.id;
                  return (
                    <button
                      key={workflow.id}
                      type="button"
                      onClick={() => {
                        setEditingWorkflowId(workflow.id);
                        setForm({
                          companyId: workflow.companyId,
                          workspaceKey: workflow.workspaceKey,
                          category: workflow.category,
                          name: workflow.name,
                          description: workflow.description,
                          triggerType: workflow.trigger.type,
                          triggerEntityType: workflow.trigger.entityType,
                          triggerEntityLabel: workflow.trigger.entityLabel,
                          triggerFieldName: workflow.trigger.fieldName || "",
                          triggerSchedule: workflow.trigger.schedule || "",
                          conditionField: workflow.conditions[0]?.field || "status",
                          conditionOperator: workflow.conditions[0]?.operator || "is",
                          conditionValue: workflow.conditions[0]?.value || "active",
                          actionType: workflow.actions[0]?.type || "Send Email",
                          actionLabel: workflow.actions[0]?.label || "",
                          actionTarget: workflow.actions[0]?.target || "",
                        });
                      }}
                      className={`rounded-3xl border p-4 text-left transition ${
                        active ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-lg font-black text-white">{workflow.name}</p>
                          <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#9AB2CB]">
                            {workflow.companyName} • {workflow.sourceWorkspace}
                          </p>
                        </div>
                        <BadgeBadge label={workflow.status} />
                      </div>
                      <p className="mt-3 text-sm leading-7 text-[#C7D6EB]">{workflow.description}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <MiniTag>{summary.triggerLabel}</MiniTag>
                        <MiniTag>{summary.conditionSummary}</MiniTag>
                        <MiniTag>{summary.actionSummary}</MiniTag>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Panel>
          </div>
        </section>

        <section className="grid gap-6 border-t border-[rgba(96,165,250,.12)] pt-8 lg:grid-cols-3">
          <Panel title="Upcoming scheduled actions" eyebrow="Queue" icon={Clock3}>
            <div className="grid gap-3">
              {globalDashboard.upcomingScheduledActions.map((workflow) => (
                <div key={workflow.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-black text-white">{workflow.name}</p>
                    <span className="rounded-full border border-cyan-400/30 px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-300">
                      {workflow.category}
                    </span>
                  </div>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#9AB2CB]">
                    {workflow.companyName} • {workflow.nextRunAt ? formatDate(workflow.nextRunAt) : "No schedule"}
                  </p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Scope summary" eyebrow="Multi-company architecture" icon={Users}>
            <div className="grid gap-3">
              <DetailRow label="Selected company" value={selectedCompany?.companyName || "All companies"} />
              <DetailRow label="Selected workspace" value={selectedWorkspace} />
              <DetailRow label="Selected category" value={selectedCategory} />
              <DetailRow label="Recent runs" value={globalDashboard.recentRuns.length} />
              <DetailRow label="Success rate" value={`${globalDashboard.successRate}%`} />
            </div>
          </Panel>

          <Panel title="Activity readiness" eyebrow="Launch planning" icon={AlertTriangle}>
            <div className="grid gap-3">
              <DetailRow label="Email automations" value="Ready" />
              <DetailRow label="Task automations" value="Ready" />
              <DetailRow label="Client automations" value="Ready" />
              <DetailRow label="Project automations" value="Ready" />
              <DetailRow label="AR recovery automations" value="Ready" />
              <DetailRow label="Kreatethedate automations" value="Ready" />
            </div>
          </Panel>
        </section>
      </div>
    </BusinessCenterOSAdminShell>
  );
}

function useAutomationBranding(title: string, description: string) {
  useEffect(() => {
    document.title = `${title} | Business Center OS`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#07131F");
    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
    };
  }, [description, title]);
}

function formToWorkflow(
  form: AutomationFormState,
  workflows: BusinessCenterAutomationWorkflowRecord[],
  editingWorkflowId: string,
): BusinessCenterAutomationWorkflowRecord {
  const selected = workflows.find((workflow) => workflow.id === editingWorkflowId);
  const company = loadBusinessCenterCompanies().find((item) => item.id === form.companyId) || loadBusinessCenterCompanies().find((item) => item.companyType !== "parent");
  if (!company) throw new Error("Select a company before saving the workflow.");
  const next = createBusinessCenterAutomationWorkflow(form);
  return {
    ...next,
    id: selected?.id || next.id,
    createdAt: selected?.createdAt || next.createdAt,
    updatedAt: new Date().toISOString(),
    successCount: selected?.successCount || next.successCount,
    failureCount: selected?.failureCount || next.failureCount,
    lastRunAt: selected?.lastRunAt || next.lastRunAt,
    nextRunAt: selected?.nextRunAt || next.nextRunAt,
    sourceModule: selected?.sourceModule || next.sourceModule,
    sourceWorkspace: selected?.sourceWorkspace || next.sourceWorkspace,
  };
}

function workspaceForCompany(company: BusinessCenterCompanyRecord): AutomationFormState["workspaceKey"] {
  if (company.brand === "kreatethedate") return "kreatethedate";
  if (company.brand === "ar_recovery") return "ar_recovery";
  return "truevance_connect";
}

function Panel({
  title,
  eyebrow,
  icon: Icon,
  children,
}: {
  title: string;
  eyebrow: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <article className="rounded-[1.75rem] border border-[rgba(96,165,250,.16)] bg-[rgba(6,13,22,.76)] p-5 shadow-[0_20px_52px_rgba(0,0,0,.18)]">
      <div className="flex items-center gap-3">
        <span className="inline-flex size-9 items-center justify-center rounded-full border border-[rgba(96,165,250,.24)] bg-[rgba(96,165,250,.08)] text-[#93C5FD]">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <div>
          <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#93C5FD]">{eyebrow}</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-white">{title}</h2>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </article>
  );
}

function MetricCard({ label, value, note }: { label: string; value: string | number; note: string }) {
  return (
    <article className="rounded-[1.5rem] border border-[rgba(96,165,250,.16)] bg-[rgba(255,255,255,.04)] p-4">
      <p className="text-[0.62rem] font-black uppercase tracking-[0.22em] text-[#93C5FD]">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold text-white">{value}</p>
      <p className="mt-2 text-sm font-semibold leading-6 text-[#C4D7ED]">{note}</p>
    </article>
  );
}

function StatChip({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-2xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] px-4 py-3">
      <p className="text-[0.65rem] font-black uppercase tracking-[0.2em] text-[#93C5FD]">{label}</p>
      <p className="mt-1 text-2xl font-black text-white">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.18em] text-[#93C5FD]">{label}</span>
      {children}
    </label>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border px-4 py-3 text-left transition ${
        active ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10"
      }`}
    >
      <p className="text-sm font-black text-white">{label}</p>
    </button>
  );
}

function BadgeBadge({ label }: { label: string }) {
  const tone =
    label === "Active"
      ? "border-emerald-400/30 text-emerald-300"
      : label === "Draft"
        ? "border-cyan-400/30 text-cyan-300"
        : label === "Paused"
          ? "border-amber-400/30 text-amber-300"
          : "border-rose-400/30 text-rose-300";
  return <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] ${tone}`}>{label}</span>;
}

function MiniTag({ children }: { children: ReactNode }) {
  return <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#B8D6FF]">{children}</span>;
}

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
      <span className="text-[0.66rem] font-black uppercase tracking-[0.18em] text-[#93C5FD]">{label}</span>
      <span className="text-sm font-bold text-white">{value}</span>
    </div>
  );
}

function StatusBanner({ tone, children }: { tone: "success" | "error"; children: ReactNode }) {
  return (
    <div
      className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
        tone === "success" ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100" : "border-rose-400/30 bg-rose-400/10 text-rose-100"
      }`}
      role="status"
    >
      {children}
    </div>
  );
}
